import type { FC } from "react";
import { useState } from "react";

import { MRTGrid } from "@foodpilot/foods";
import { Icv } from "src/api/client-api/icv/useAllIcvs";
import { useIcvListContext } from "./IcvListContext";
import { useTranslation } from "react-i18next";
import {
    Box,
    Stack,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    CircularProgress,
} from "@mui/material";
import { FoodsIcon } from "@foodpilot/foods";
import { useDeleteIcv } from "src/api/client-api/icv/useDeleteIcv";
import { useSnackbar } from "src/utils/useSnackbar";
import { useQueryClient } from "react-query";
import { IcvModal } from "./IcvModal";

export const IcvGrid: FC = () => {
    const { t } = useTranslation();
    const { setSnackbarProps } = useSnackbar();
    const queryClient = useQueryClient();

    const {
        icvs,
        isLoading,
        mrt_columns,
        totalItems,
        icvPage,
        setIcvPage,
        sorting,
        setSorting,
        delayedSearchIcv,
        gridKey,
    } = useIcvListContext();

    // Modal and delete dialog states
    const [editingIcv, setEditingIcv] = useState<Icv | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [icvToDelete, setIcvToDelete] = useState<Icv | null>(null);

    const deleteIcvMutation = useDeleteIcv();

    // Calculate total pages
    const totalPages = Math.ceil((totalItems || 0) / icvPage.pageSize);

    // Actions
    const handleEditClick = (icv: Icv) => {
        setEditingIcv(icv);
        setShowEditModal(true);
    };

    const handleDeleteClick = (icv: Icv) => {
        setIcvToDelete(icv);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!icvToDelete) return;

        try {
            await deleteIcvMutation.mutateAsync(icvToDelete.id);
            setSnackbarProps({
                message: t("icvs.delete.success", "ICV deleted successfully"),
                type: "success",
            });
            queryClient.invalidateQueries("allIcvs");
            setDeleteDialogOpen(false);
            setIcvToDelete(null);
        } catch {
            setSnackbarProps({
                message: t("icvs.delete.error", "Failed to delete ICV"),
                type: "error",
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setIcvToDelete(null);
    };

    return (
        <>
            <MRTGrid
                data={icvs || []}
                columns={mrt_columns.mrt_columns}
                enableRowActions
                enableRowSelection={false}
                enablePagination={true}
                enableSelectAll={false}
                enableColumnPinning={true}
                displayColumnDefOptions={{
                    "mrt-row-actions": {
                        size: 80,
                        grow: false,
                    },
                }}
                renderRowActions={({ row }) => (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Box
                            component="button"
                            onClick={() => handleEditClick(row.original)}
                            sx={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                                "&:hover": {
                                    backgroundColor: "#F5F5F5",
                                },
                            }}
                        >
                            <FoodsIcon icon="edit" size={1} />
                        </Box>
                        <Box
                            component="button"
                            onClick={() => handleDeleteClick(row.original)}
                            sx={{
                                width: "32px",
                                height: "32px",
                                borderRadius: "50%",
                                border: "none",
                                backgroundColor: "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                transition: "background-color 0.2s",
                                "&:hover": {
                                    backgroundColor: "#F5F5F5",
                                },
                            }}
                        >
                            <FoodsIcon icon="delete" size={1} />
                        </Box>
                    </Stack>
                )}
                state={{
                    isLoading: isLoading,
                    pagination: icvPage,
                    sorting: sorting,
                    globalFilter: delayedSearchIcv,
                    columnVisibility: mrt_columns.columnVisibility,
                    columnOrder: mrt_columns.columnOrder,
                    columnSizing: mrt_columns.columnSizing,
                    columnPinning: {
                        left: [mrt_columns.columnOrder[0]],
                        right: ["mrt-row-actions"],
                    },
                }}
                onColumnSizingChange={mrt_columns.setColumnSizing}
                // Server-side pagination
                rowCount={totalItems}
                manualPagination={true}
                onPaginationChange={setIcvPage}
                manualSorting={true}
                onSortingChange={setSorting}
                manualFiltering={true}
                localization={{
                    actions: "",
                }}
                muiTableBodyRowProps={{
                    sx: {
                        "&:hover": {
                            backgroundColor: "#F9F9F8",
                        },
                        "& .MuiTableCell-root": {
                            paddingTop: "16px",
                            paddingBottom: "16px",
                        },
                    },
                }}
            />

            {/* Edit Modal */}
            <IcvModal
                open={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setEditingIcv(null);
                }}
                editingIcv={editingIcv || undefined}
                onSuccess={() => {
                    setShowEditModal(false);
                    setEditingIcv(null);
                    queryClient.invalidateQueries("allIcvs");
                }}
            />

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        padding: "8px",
                    },
                }}
            >
                <DialogTitle
                    sx={{ fontWeight: 700, fontSize: "1.125rem", color: "#1E1E1E", padding: "24px 24px 16px" }}
                >
                    {t("icvs.delete.title", "Delete ICV")}
                </DialogTitle>
                <DialogContent
                    sx={{
                        padding: "0 24px 24px",
                    }}
                >
                    <Typography sx={{ fontSize: "0.875rem", color: "#50504D", lineHeight: "1.6" }}>
                        {t("icvs.delete.message", "Are you sure you want to delete this ICV?")}
                        {icvToDelete && (
                            <strong>
                                {" "}
                                {icvToDelete.name} ({icvToDelete.code})
                            </strong>
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions
                    sx={{
                        padding: "16px 24px 24px",
                        gap: "12px",
                    }}
                >
                    <Button
                        onClick={handleDeleteCancel}
                        variant="outlined"
                        sx={{
                            borderRadius: "24px",
                            padding: "8px 24px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            borderColor: "#E7E6E4",
                            color: "#393A36",
                            "&:hover": {
                                borderColor: "#D0D0D0",
                                backgroundColor: "#F5F5F5",
                            },
                        }}
                    >
                        {t("common.cancel", "Cancel")}
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        disabled={deleteIcvMutation.isLoading}
                        sx={{
                            borderRadius: "24px",
                            padding: "8px 24px",
                            fontWeight: 600,
                            fontSize: "0.875rem",
                            textTransform: "none",
                            backgroundColor: "#D32F2F",
                            "&:hover": {
                                backgroundColor: "#B71C1C",
                            },
                        }}
                    >
                        {deleteIcvMutation.isLoading ?
                            <CircularProgress size={16} color="inherit" />
                        :   t("common.delete", "Delete")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
